// src/pages/BrandDetail/components/FeaturedProductsSection.js
import { Star, ImageOff } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import './FeaturedProductsSection.css';

const FeaturedProductsSection = ({ products }) => {
  const { translate } = useLanguage();
  const [imageErrors, setImageErrors] = useState({});

  if (!products || products.length === 0) return null;

  // Filter out products with no image or whose image failed to load
  const sortedProducts = [...products]
    .filter(p => p.s3_url && p.s3_url.trim() !== '' && !imageErrors[p.group_id]);

  // Get badge color based on stage
  const getStageBadgeClass = (stage) => {
    const normalizedStage = stage?.trim().toLowerCase() || '';
    if (normalizedStage.includes('성장') || normalizedStage.includes('growing')) {
      return 'stage-badge-rose';
    }
    if (normalizedStage.includes('안정') || normalizedStage.includes('stable')) {
      return 'stage-badge-emerald';
    }
    if (normalizedStage.includes('하락') || normalizedStage.includes('decreasing')) {
      return 'stage-badge-slate';
    }
    if (normalizedStage.includes('초기') || normalizedStage.includes('early')) {
      return 'stage-badge-amber';
    }
    return 'stage-badge-slate';
  };

  // Get stage text in English
  const getStageText = (stage) => {
    const normalizedStage = stage?.trim().toLowerCase() || '';
    if (normalizedStage.includes('성장')) return 'Growing';
    if (normalizedStage.includes('안정')) return 'Stable';
    if (normalizedStage.includes('하락')) return 'Decreasing';
    if (normalizedStage.includes('초기')) return 'Early';
    if (normalizedStage.includes('변화')) return 'Changing';
    return stage || '';
  };

  const handleImageError = (productId) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  };

  const handleProductClick = (product) => {
    if (product.group_id) {
      window.open(`/product/${product.group_id}`, '_blank');
    }
  };

  return (
    <section className="featured-products-section">
      {/* Header */}
      <div className="featured-products-header">
        <div>
          <h2 className="font-serif featured-products-title">
            {translate('brandDetailPage.sections.featuredProducts') || 'Featured Products'}
          </h2>
          <p className="featured-products-subtitle">Curated selection of our best performers</p>
        </div>
        <button className="view-all-button">
          View All Products
        </button>
      </div>

      {/* Masonry Grid */}
      <div className="products-masonry-grid">
        {sortedProducts.map((product, index) => {
          const rating = product['평점'] || product.rating || '0.0';
          const reviews = parseInt(product['리뷰수'] || product.reviews || 0).toLocaleString();
          const stage = product['단계'] || product.stage;
          const productName = product['제품명'] || product.name || 'Unknown Product';
          const brandName = product['브랜드명'] || product.brand || '';
          const hasImage = product.s3_url && !imageErrors[product.group_id];

          return (
            <div
              key={product.group_id || index}
              className="product-card"
              onClick={() => handleProductClick(product)}
            >
              {/* Image Container */}
              <div className="product-image-container">
                {/* Stage Badge */}
                {stage && (
                  <span className={`stage-badge ${getStageBadgeClass(stage)}`}>
                    {getStageText(stage)}
                  </span>
                )}

                {/* Product Image */}
                {hasImage ? (
                  <img
                    src={product.s3_url}
                    alt={productName}
                    className="product-image"
                    onError={() => handleImageError(product.group_id)}
                  />
                ) : (
                  <div className="product-image-fallback">
                    <ImageOff className="fallback-icon" />
                  </div>
                )}

                {/* Hover Overlay */}
                <div className="product-image-overlay"></div>
              </div>

              {/* Product Info */}
              <div className="product-info">
                {/* Rating */}
                <div className="product-rating">
                  <Star className="star-icon" />
                  <span className="rating-value">{parseFloat(rating).toFixed(1)}</span>
                  <span className="rating-count">({reviews})</span>
                </div>

                {/* Product Name */}
                <h3 className="product-name font-serif">{productName}</h3>

                {/* Brand Name */}
                {brandName && <p className="product-brand">{brandName}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FeaturedProductsSection;
